import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { deveTentarDeNovo, ehFalhaPassageira, esperaDaTentativa, FALHA_DE_REDE } from './falha-de-rede';
import { explicarFalha } from './pedido';
import { ehLeituraSemLicenca } from './licenca-das-rotas';
import { anunciar, fatosValidos, type MontagemDoRetorno } from './retorno';

/**
 * Cliente unico de consulta — e o lugar onde TODA chamada avisa o que aconteceu.
 *
 * ## O retorno padrao mora aqui, e nao nas telas
 *
 * Antes disto cada tela escolhia sozinha se avisava, e a maioria nao avisava:
 * gravar com sucesso fechava a janela e nada mais, e quem clicou ficava sem
 * saber se o clique pegou. Um aviso por tela e um aviso que a tela seguinte
 * esquece. Aqui ele vale para o que ja existe e para o que ainda vai ser
 * escrito, sem ninguem precisar lembrar — a mesma razao pela qual o isolamento
 * entre empresas mora no banco (ADR-002).
 *
 * ## O que avisa, e o que nao avisa
 *
 * - **Toda escrita** (`useMutation`) avisa: deu certo e deu errado. O que deu
 *   certo diz o que a PROPRIA escrita declarou no `meta.retorno` — titulo,
 *   alvo, fatos e efeito (R04, p.32). Nao ha texto padrao: a escrita que nao
 *   declara nao anuncia nada, e o `cliente-consulta.test.ts` a reprova.
 * - **Leitura** (`useQuery`) avisa so quando FALHA. Leitura que deu certo nao
 *   e novidade — a tela encheu, e isso ja e o aviso.
 * - **Sessao vencida nao avisa.** Ela leva para a entrada, e uma faixa
 *   vermelha sobre a tela de login diria a quem acabou de ser deslogado que
 *   ele fez algo errado.
 * - **Leitura cancelada nao avisa.** Trocar de tela com consulta em voo nao e
 *   falha de ninguem.
 */

/** O que o `meta` de uma mutacao pode declarar. */
declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      /**
       * O retorno do que deu certo (R04, p.32), montado da resposta e das
       * variaveis: `retornoDe((resposta, variaveis) => ({ titulo, alvo, fatos, efeito }))`.
       */
      readonly retorno?: MontagemDoRetorno;
      /** `true` cala o aviso de sucesso — para a escrita que a propria tela ja narra. */
      readonly silencioso?: boolean;
      /**
       * `true` cala o aviso de FALHA — para o formulario que mostra a recusa no
       * aviso do topo dele (regra do campo, p.22). Sem isto a mesma recusa
       * aparecia duas vezes: no formulario e no toast.
       */
      readonly silenciarFalha?: boolean;
    };
    queryMeta: {
      /** O que a tela estava tentando ler, para a falha dizer O QUE falhou. */
      readonly oQueLia?: string;
      /**
       * A leitura opcional, cuja falha a tela ja trata sem aviso: a imagem da
       * folha na conferencia do celular (M14) — sem ela, "Ampliar" so nao
       * aparece, e o aviso cobriria o botao de confirmar.
       */
      readonly silenciarFalha?: boolean;
    };
  }
}

/** A recusa da API, nas duas copias que existem (`lib/pedido.ts` e o api.ts do modulo). */
interface RecusaLida {
  readonly nome: string;
  readonly texto: string;
  readonly comoResolver: string | undefined;
  readonly status: number | undefined;
}

function lerErro(erro: unknown): RecusaLida {
  const e = erro as { name?: string; message?: string; comoResolver?: string; status?: unknown } | null;
  return {
    /* As camadas de pedido antigas (equipamentos, cadastros…) levantam o 401
       como `ErroDaApi` com `status`, e nao como `SemSessao`: sem isto, a lista
       que carregou antes do login virava faixa "Sua sessao nao esta aberta"
       que continuava na tela DEPOIS de entrar. */
    nome: e?.status === 401 ? 'SemSessao' : (e?.name ?? ''),
    texto: (e?.message ?? '').trim() || 'a chamada não foi aceita',
    /* O 403 diz o PERFIL a pedir, como o estado sem permissão da tela (prova
       do marco, 07/10/2026): o servidor nomeia só a licença. */
    comoResolver: e?.status === 403 ? explicarFalha(erro).comoResolver : typeof e?.comoResolver === 'string' ? e.comoResolver : undefined,
    status: typeof e?.status === 'number' ? e.status : undefined,
  };
}

/** O codigo que o servidor manda na recusa do posto parceiro (`autorizacao/parceiro.ts`). */
export const CODIGO_DO_POSTO_PARCEIRO = 'acesso_de_posto_parceiro';

export function ehRecusaDoPostoParceiro(erro: unknown): boolean {
  const e = erro as { status?: unknown; codigo?: unknown } | null;
  return e?.status === 403 && e.codigo === CODIGO_DO_POSTO_PARCEIRO;
}

/* Sessao vencida e cancelamento saem sem faixa — ver o cabecalho. */
function calado(nome: string): boolean {
  return nome === 'SemSessao' || nome === 'CancelledError' || nome === 'AbortError';
}

/**
 * A FALHA DE REDE ESPERADA (0129): o lancamento do combustivel aberto sem
 * sinal, pela declaracao do ultimo login (`ProvedorSemSinal`). Ali o servidor
 * esta fora de alcance por definicao, a faixa "Sem conexao" ja diz isso, e as
 * leituras que a tela tenta (cadastros, lista) cairiam no toast "O sistema nao
 * respondeu" a cada tentativa. So a falha PASSAGEIRA se cala; a recusa do
 * servidor continua avisando.
 */
let falhaDeRedeEsperada = false;
export function esperarFalhaDeRede(sim: boolean): void {
  falhaDeRedeEsperada = sim;
}

export const clienteConsulta = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      /* A falha passageira (rede, sistema reiniciando atras do proxy) tenta de
         novo sozinha, com espera crescente, antes de virar aviso; a recusa do
         servidor nao repete. Ver `falha-de-rede.ts` (item 3 dos robos). */
      retry: deveTentarDeNovo,
      retryDelay: esperaDaTentativa,
    },
  },

  mutationCache: new MutationCache({
    onSuccess: (dado, variaveis, _contexto, mutacao) => {
      if (mutacao.meta?.silencioso === true) return;
      const montar = mutacao.meta?.retorno;
      if (montar === undefined) {
        /* Sem texto padrao (R04): o teste reprova a escrita que chega aqui. */
        if (import.meta.env.DEV) console.error('Mutacao sem meta.retorno nem silencioso: declare o titulo do retorno.');
        return;
      }
      /* Uma falha ao MONTAR o aviso nao pode virar falha da gravacao: o
         MutationCache espera este retorno, e uma excecao aqui poria em erro a
         mutacao que o servidor ja aceitou. */
      try {
        const d = montar(dado, variaveis);
        const fatos = fatosValidos(d.fatos);
        anunciar({
          tom: d.tom ?? 'sucesso',
          titulo: d.titulo,
          ...(d.alvo ? { alvo: d.alvo } : {}),
          ...(fatos.length > 0 ? { fatos } : {}),
          ...(d.efeito === undefined ? {} : { efeito: d.efeito }),
          ...(d.codigo === undefined ? {} : { codigo: d.codigo }),
          ...(d.ver === undefined ? {} : { acoes: [{ rotulo: 'Ver', rota: d.ver }] }),
        });
      } catch (erro) {
        console.error('O retorno da gravacao nao pode ser montado.', erro);
      }
    },
    onError: (erro, _variaveis, _contexto, mutacao) => {
      if (mutacao.meta?.silenciarFalha === true) return;
      const { nome, texto, comoResolver, status } = lerErro(erro);
      if (calado(nome)) return;
      const recusou = typeof status === 'number' && status >= 400 && status < 500;
      anunciar({
        tom: 'falha',
        /* A primeira letra em maiuscula: a frase do servidor vem em minuscula
           porque nasceu para ser encaixada numa outra ("o lancamento nao
           atende..."), e aqui ela e a frase inteira. */
        titulo: texto.charAt(0).toUpperCase() + texto.slice(1),
        ...(comoResolver === undefined ? {} : { comoResolver }),
        /* "Nada foi gravado" (p.32, nao fez) so quando o SERVIDOR respondeu a
           recusa: a transacao dele nao passou. Sem resposta (rede caiu no meio)
           nao se sabe, e o toast nao afirma. */
        ...(recusou ? { nota: 'Nada foi gravado' } : {}),
      });
    },
  }),

  queryCache: new QueryCache({
    onError: (erro, consulta) => {
      if (consulta.meta?.silenciarFalha === true) return;
      /* A leitura que nem saiu por falta de licenca (`licenca-das-rotas`): o
         que o perfil nao le e o estado sem permissao da tela, nunca toast. */
      if (ehLeituraSemLicenca(erro)) return;
      /* A leitura de fundo que o servidor recusa a quem e de posto parceiro
         (0132): a pessoa so tem o lancamento, e ela nao pediu essa lista. O
         toast a cada entrada era aviso inutil (dono, 31/10/2026). */
      if (ehRecusaDoPostoParceiro(erro)) return;
      const { nome, texto, comoResolver } = lerErro(erro);
      if (calado(nome)) return;
      /* A FALHA PASSAGEIRA E UMA FRASE SO, igual para toda consulta: cinco
         leituras que caem juntas na implantacao davam cinco toasts "Nao foi
         possivel carregar…"; com o texto igual, o `anunciar` mostra um. */
      if (ehFalhaPassageira(erro)) {
        if (falhaDeRedeEsperada) return;
        anunciar({ tom: 'falha', titulo: FALHA_DE_REDE.titulo, comoResolver: FALHA_DE_REDE.comoResolver, daLeitura: true });
        return;
      }
      const oQue = consulta.meta?.oQueLia;
      anunciar({
        tom: 'falha',
        /* A falha de leitura e da tela: sai quando ela sai (`trocouDeTela`). */
        daLeitura: true,
        titulo: oQue === undefined ? texto.charAt(0).toUpperCase() + texto.slice(1) : `Não foi possível carregar ${oQue}.`,
        comoResolver:
          comoResolver ??
          'Tente carregar de novo; se continuar, avise o suporte com o horário em que aconteceu.',
      });
    },
  }),
});
